import {
  Button,
  Form,
  Modal,
  Input,
  Divider,
  Row,
  InputNumber,
  Select,
  message,
} from "antd";
import { PlusOutlined, MinusCircleOutlined } from "@ant-design/icons";
import { useRef, useState } from "react";
import { db } from "../../firebase";
import {
  collection,
  getDocs,
  serverTimestamp,
  Timestamp,
  doc,
  increment,
  writeBatch,
} from "firebase/firestore";
import ResponsiveDatePicker from "./responsiveDatePicker";

const getNetflixScreensForOption = (optionName) => {
  const normalizedName = String(optionName || "")
    .toLowerCase()
    .replace(/[\s/+_-]/g, "");

  if (normalizedName.includes("ทุกอุปกรณ์") || normalizedName.includes("ทีวี")) {
    return [1, 2, 3];
  }

  if (
    normalizedName.includes("มือถือ") ||
    normalizedName.includes("แท็บเล็ต") ||
    normalizedName.includes("คอม")
  ) {
    return [4, 5];
  }

  return null;
};

export default function CreateProductAdminPage({
  groupId,
  inventory,
  appList,
  refreshInventory,
}) {
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const creatingRef = useRef(false);
  const isNetflixApp = appList?.nameLower === "netflix";
  const configuredScreenCount =
    Number.isInteger(Number(appList?.screenCount)) &&
    Number(appList?.screenCount) > 0
      ? Number(appList.screenCount)
      : null;

  const optionIdSelect = Form?.useWatch("optionId", form);

  const showModal = () => {
    setOpen(true);
    form.setFieldsValue({
      optionId: null,
      products: [
        {
          email: "",
          password: "",
          content: "",
          amount: "",
          permanent: false,
        },
      ],
    });
  };

  const createOptionProductItems = async (value) => {
    if (creatingRef.current) return;
    creatingRef.current = true;
    setCreating(true);

    try {
      if (isNetflixApp) {
        const optionsSnapshot = await getDocs(
          collection(db, "productGroups", groupId, "options"),
        );
        const netflixTargets = optionsSnapshot.docs
          .filter((optionDoc) => optionDoc.data()?.status !== "deleted")
          .map((optionDoc) => ({
            optionDoc,
            screens: getNetflixScreensForOption(optionDoc.data()?.name),
          }))
          .filter((target) => target.screens);

        const allDeviceTarget = netflixTargets.find((target) =>
          target.screens.includes(1),
        );
        const mobileTarget = netflixTargets.find((target) =>
          target.screens.includes(4),
        );

        if (!allDeviceTarget || !mobileTarget) {
          message.error(
            "ไม่พบตัวเลือก Netflix: ทุกอุปกรณ์+ทีวี และ มือถือ/แท็บเล็ต/คอม",
          );
          return;
        }

        const batch = writeBatch(db);
        const targets = [allDeviceTarget, mobileTarget];

        targets.forEach(({ optionDoc, screens }) => {
          const productsCollection = collection(
            db,
            "productGroups",
            groupId,
            "options",
            optionDoc.id,
            "products",
          );

          value.products.forEach((item) => {
            const screenStatus = Object.fromEntries(
              screens.map((screen) => [
                String(screen),
                { status: "available" },
              ]),
            );

            batch.set(doc(productsCollection), {
              email: item.email,
              password: item.password,
              amount: screens.length,
              remainAmount: screens.length,
              permanent: false,
              status: "available",
              expiredAt: Timestamp.fromDate(item.expiredAt.toDate()),
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
              screenStatus,
            });
          });

          batch.update(optionDoc.ref, {
            stock: increment(screens.length * value.products.length),
            updatedAt: serverTimestamp(),
          });
        });

        await batch.commit();
        form.resetFields();
        await refreshInventory?.();
        setOpen(false);
        return;
      }

      if (!value?.optionId) {
        message?.error("กรุณาเลือกตัวเลือกสินค้า");
        return;
      }
      const productsCollection = collection(
        db,
        "productGroups",
        groupId,
        "options",
        value?.optionId,
        "products",
      );
      const batch = writeBatch(db);

      value.products.forEach((item) => {
        const isPermanentApp = appList?.type === "permanent";
        const amount = isPermanentApp
          ? 1
          : configuredScreenCount || Number(item.amount || 0);

        const screenStatus = {};
        if (isPermanentApp) {
          // สินค้าถาวรไม่มี Profile
        } else {
          for (let i = 1; i <= amount; i++) {
            screenStatus[i] = {
              status: "available",
            };
          }
        }
        const payload = {
          ...(!isPermanentApp ? { email: item.email } : {}),
          amount,
          remainAmount:
            isPermanentApp
              ? 1
              : amount,
          permanent: isPermanentApp,
          status: "available",
          expiredAt: isPermanentApp
            ? null
            : Timestamp.fromDate(item.expiredAt.toDate()),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          screenStatus,
        };

        if (isPermanentApp) {
          payload.content = item.content?.trim();
        } else if (appList?.type === "email") {
          payload.password = item.password;
        } else if (appList?.type === "phone") {
          payload.phone = item.phone;
        }
        batch.set(doc(productsCollection), payload);
      });

      const sumAmount = value.products.reduce(
        (sum, currentValue) =>
          sum +
          (appList?.type === "permanent"
            ? 1
            : configuredScreenCount
            ? configuredScreenCount
            : Number(currentValue?.amount)),
        0,
      );

      const optionRef = doc(
        db,
        "productGroups",
        groupId,
        "options",
        value?.optionId,
      );

      batch.update(optionRef, {
        stock: increment(sumAmount),
        updatedAt: serverTimestamp(),
      });
      await batch.commit();
      form?.resetFields();
      await refreshInventory?.();
      setOpen(false);
    } catch (error) {
      console.error(error);
    } finally {
      creatingRef.current = false;
      setCreating(false);
    }
  };

  return (
    <>
      <Button
        onClick={showModal}
        className="neo-btn-create-admin"
        icon={<PlusOutlined />}
      />

      {/* MODAL */}
      <Modal
        width={500}
        open={open}
        footer={false}
        closeIcon={false}
        centered
        className="neo-modal"
      >
        <div className="neo-modal-header">
          <div>
            <div className="neo-modal-topline">MANAGEMENT</div>

            <div className="neo-modal-title">เพิ่มสินค้า</div>
          </div>

          <Button
            className="neo-close-btn"
            onClick={() => {
              setOpen(false);
              form?.resetFields();
            }}
          >
            ✕
          </Button>
        </div>

        <Divider
          style={{
            borderColor: "rgba(255,255,255,0.08)",
            margin: "18px 0px",
          }}
        />

        <Form form={form} layout="vertical" onFinish={createOptionProductItems}>
          {isNetflixApp ? null : <Form.Item
            name="optionId"
            rules={[
              {
                required: true,
                message: "กรุณาเลือก ตัวเลือกสินค้า",
              },
            ]}
          >
            <Select
              options={inventory?.map((iv) => {
                return {
                  label: iv?.optionName,
                  value: iv?.optionId,
                };
              })}
              popupClassName="neo-select-dropdown"
              placeholder="เลือก ตัวเลือกสินค้า"
              className="neo-input-modal"
            />
          </Form.Item>}
          <Form.List name="products">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, index) => {
                  return (
                    <div key={field.key} className="neo-subject-card">
                      <div className="neo-subject-head">
                        <span>บัญชี #{index + 1}</span>

                        {index > 0 && (
                          <MinusCircleOutlined
                            className="neo-remove-icon"
                            onClick={() => remove(index)}
                          />
                        )}
                      </div>

                      {appList?.type === "permanent" ? (
                        <Form.Item
                          {...field}
                          name={[field.name, "content"]}
                          rules={[{ required: true, message: "กรุณากรอกสินค้า" }]}
                          style={{ marginBottom: 10 }}
                        >
                          <Input.TextArea
                            className="neo-input-modal"
                            placeholder="กรอกข้อมูลสินค้า"
                            autoSize={{ minRows: 4, maxRows: 10 }}
                          />
                        </Form.Item>
                      ) : <Form.Item
                        {...field}
                        name={[field.name, "email"]}
                        rules={[
                          {
                            required: true,
                            message: "กรุณากรอก Email",
                          },
                        ]}
                        style={{
                          flex: 1,
                          marginBottom: 10,
                        }}
                      >
                        <Input
                          className="neo-input-modal"
                          placeholder="กรอก Email"
                        />
                      </Form.Item>}
                      {appList?.type === "permanent" ||
                      (appList?.type === "onlyEmail" && !isNetflixApp) ? null :
                      appList?.type === "email" || isNetflixApp ? (
                        <Form.Item
                          {...field}
                          name={[field.name, "password"]}
                          rules={[
                            {
                              required: true,
                              message: "กรุณากรอก Password",
                            },
                          ]}
                          style={{
                            flex: 1,
                            marginBottom: 10,
                          }}
                        >
                          <Input
                            className="neo-input-modal"
                            placeholder="กรอก Password"
                          />
                        </Form.Item>
                      ) : (
                        <Form.Item
                          {...field}
                          name={[field.name, "phone"]}
                          rules={[
                            {
                              required: true,
                              message: "กรุณากรอก เบอร์โทร",
                            },
                            {
                              pattern: /^[0-9]{8,15}$/,
                              message: "รูปแบบเบอร์โทรไม่ถูกต้อง",
                            },
                          ]}
                          style={{
                            flex: 1,
                            marginBottom: 10,
                          }}
                        >
                          <Input
                            inputMode="numeric"
                            className="neo-input-modal"
                            placeholder="กรอก เบอร์โทร"
                          />
                        </Form.Item>
                      )}
                      {appList?.type === "permanent" ||
                      appList?.nameLower === "netflix" ||
                      configuredScreenCount ? null : (
                        <Form.Item
                          noStyle
                          shouldUpdate={(prevValues, curValues) =>
                            prevValues.products?.[field.name]?.permanent !==
                            curValues.products?.[field.name]?.permanent
                          }
                          style={{
                            flex: 1,
                            marginBottom: 0,
                          }}
                        >
                          {({ getFieldValue }) => {
                            const permanent = getFieldValue([
                              "products",
                              field.name,
                              "permanent",
                            ]);

                            if (permanent) return null;

                            return (
                              <Form.Item
                                {...field}
                                name={[field.name, "amount"]}
                                rules={[
                                  {
                                    required: true,
                                    message: "กรุณากรอก จำนวนจอ",
                                  },
                                ]}
                                style={{ marginBottom: 0 }}
                              >
                                <InputNumber
                                  style={{ width: "100%" }}
                                  className="neo-input-modal"
                                  placeholder="กรอก จำนวนจอ"
                                  inputMode="numeric"
                                  min={1}
                                  max={100}
                                />
                              </Form.Item>
                            );
                          }}
                        </Form.Item>
                      )}

                      {appList?.type !== "permanent" &&
                      !isNetflixApp &&
                      configuredScreenCount ? (
                        <div
                          style={{
                            marginBottom: 10,
                            color: "#fbbf24",
                            fontWeight: 700,
                          }}
                        >
                          ระบบจะสร้าง {configuredScreenCount} จอต่อบัญชี
                        </div>
                      ) : null}

                      {appList?.type !== "permanent" && <Form.Item
                        {...field}
                        className="neo-expiration-date-field"
                        name={[field.name, "expiredAt"]}
                        rules={[
                          {
                            required: true,
                            message: "กรุณาเลือก วันหมดอายุ",
                          },
                        ]}
                        style={{
                          flex: 1,
                          marginBottom: 10,
                        }}
                      >
                        <ResponsiveDatePicker
                          style={{ width: "100%" }}
                          className="neo-input-modal"
                          popupClassName="neo-fold-date-picker-popup"
                          getPopupContainer={(triggerNode) =>
                            triggerNode.closest(".ant-modal-wrap") ||
                            document.body
                          }
                          placeholder="วันหมดอายุ"
                          inputReadOnly
                        />
                      </Form.Item>}
                    </div>
                  );
                })}

                <Form.Item style={{ marginTop: 10 }}>
                  <Button
                    type="dashed"
                    onClick={() =>
                      add({
                        permanent: false,
                      })
                    }
                    icon={<PlusOutlined />}
                    className="neo-dashed-btn"
                  >
                    เพิ่มสินค้า
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>

          {/* FOOTER BUTTON */}
          <Row justify="space-between" gutter={[12, 12]}>
            <Button
              className="neo-cancel-btn"
              disabled={creating}
              onClick={() => {
                setOpen(false);
                form?.resetFields();
              }}
            >
              ยกเลิก
            </Button>

            <Button
              htmlType="submit"
              className="neo-save-btn"
              loading={creating}
              disabled={creating || (!isNetflixApp && !optionIdSelect)}
            >
              บันทึก
            </Button>
          </Row>
        </Form>
      </Modal>

      {/* STYLE */}
      <style>{`
        /* OPEN BUTTON */
    
        .neo-btn-create-admin{
        padding:0 10px !important;

          background:rgba(59,130,246,0.15) !important;
          border:1px solid rgba(59,130,246,0.3) !important;
          color:#60a5fa !important;
            border-radius:12px !important;
            font-size:14px !important;
            font-weight:700 !important;
             transition:.25s;
        }

        .neo-btn-create-admin:hover{
          transform:translateY(-2px);
        }

        /* MODAL */
        .neo-modal .ant-modal-content{
          background:
            linear-gradient(
              135deg,
              rgba(255,255,255,.14),
              rgba(255,255,255,.05)
            ) !important;

          border:1px solid rgba(255,255,255,.08);

          backdrop-filter:blur(26px);

          border-radius:30px;

          padding:26px;

          overflow:hidden;

          box-shadow:
            0 25px 60px rgba(0,0,0,.28);
        }

        .neo-modal .ant-modal-mask{
          backdrop-filter:blur(6px);
        }

        /* HEADER */
        .neo-modal-header{
          display:flex;
          justify-content:space-between;
          align-items:flex-start;
          gap:16px;
        }

        .neo-modal-topline{
          color:#fc950f;
          font-size:12px;
          font-weight:800;
          letter-spacing:3px;
        }

        .neo-modal-title{
          color:white;
          font-size:28px;
          font-weight:900;
          margin-top:4px;
        }

        .neo-close-btn{
          width:42px;
          height:42px;

          border:none !important;

          border-radius:14px !important;

          background:
            rgba(255,255,255,.08) !important;

          color:white !important;
        }

        /* SUBJECT CARD */
        .neo-subject-card{
          padding:18px;

          border-radius:22px;

          background:
            rgba(255,255,255,.05);

          border:1px solid rgba(255,255,255,.06);

          margin-bottom:16px;
        }

        .neo-subject-head{
          display:flex;
          justify-content:space-between;
          align-items:center;

          margin-bottom:14px;

          color:white;

          font-weight:800;
        }

        .neo-remove-icon{
          color:#f87171;
          font-size:18px;
          cursor:pointer;
        }

        /* INPUT */
        .neo-input-modal{
  height:52px !important;

  border:1px solid rgba(255,255,255,.08) !important;
  outline:none !important;

  border-radius:16px !important;

  background:
    rgba(255,255,255,.06) !important;

  color:#fff !important;
  font-weight:600;

  box-shadow:none !important;
}

        /* SELECT */
        .neo-select .ant-select-selector{
          height:52px !important;

          border:none !important;

          border-radius:16px !important;

          background:
            rgba(255,255,255,.92) !important;

          display:flex;
          align-items:center;
        }

        /* ADD BTN */
        .neo-dashed-btn{
          width:100%;
          height:54px;

          border-radius:18px !important;

          background:
            rgba(255,255,255,.05) !important;

          border:1px dashed rgba(255,255,255,.15) !important;

          color:white !important;

          font-weight:700;
        }

        /* FOOTER BUTTON */
        .neo-cancel-btn{
          width:48%;
          height:54px;

          border:none !important;

          border-radius:18px !important;

          background:
            rgba(255,255,255,.08) !important;

          color:white !important;

          font-weight:700;
        }

        .neo-save-btn{
          width:48%;
          height:54px;

          border:none !important;

          border-radius:18px !important;

          background:
    linear-gradient(
      135deg,
      #fbbf24,
      #dc2626
    ) !important;

          color:white !important;

          font-weight:800;

          box-shadow:
            0 18px 45px rgba(220,38,38,.45);;
        }

        /* MOBILE */
        @media (max-width: 768px), (min-width: 769px) and (max-width: 1366px) and (pointer: coarse){

          .neo-modal .ant-modal-content{
            padding:20px;
            border-radius:24px;
          }

          .neo-modal-title{
            font-size:22px;
          }

          // .neo-cancel-btn,
          // .neo-save-btn{
          //   width:100%;
          // }
        }
        /* InputNumber */

.neo-input-modal.ant-input-number{
  width:100% !important;
  height:52px !important;

  border:1px solid rgba(255,255,255,.08) !important;
  outline:none !important;

  border-radius:16px !important;

  background:
    rgba(255,255,255,.06) !important;

  overflow:hidden;
  color: white !important;
}

.neo-input-modal.ant-input-number .ant-input-number-input-wrap{
  height:100%;
}

.neo-input-modal.ant-input-number .ant-input-number-input{
  height:52px !important;

  padding:0 14px !important;

  font-weight:600;

  background:transparent !important;
}

.neo-input-modal.ant-input-number:hover,
.neo-input-modal.ant-input-number-focused{
  border:none !important;

  box-shadow:
    0 0 0 2px rgba(255,255,255,.08) !important;
}

.ant-input-number.neo-input-modal .ant-input-number-input {
  color: #fff !important;
}

.ant-input-number.neo-input-modal .ant-input-number-input::placeholder {
  color: #94a3b8 !important;
}

/* ปุ่ม + - */

.neo-input-modal .ant-input-number-handler-wrap{
  background:
    rgba(255,255,255,.08);
}

.neo-input-modal .ant-input-number-handler{
  border:none !important;
}

.neo-input-modal .ant-input-number-handler-up,
.neo-input-modal .ant-input-number-handler-down{
  color:#fbbf24 !important;
}


/* DatePicker */

.neo-input-modal.ant-picker {
  width: 100% !important;
  height: 52px !important;

  background: rgba(255,255,255,.06) !important;
  border: 1px solid rgba(255,255,255,.08) !important;
  border-radius: 16px !important;

  color: #fff !important;
}

.neo-input-modal.ant-picker:hover,
.neo-input-modal.ant-picker-focused {
  border-color: transparent !important;
  box-shadow: 0 0 0 2px rgba(255,255,255,.08) !important;
}

/* ข้อความ */
.neo-input-modal.ant-picker input {
  color: #fff !important;
  -webkit-text-fill-color: #fff !important;
  font-weight: 600;
}

/* Placeholder */
.neo-input-modal.ant-picker input::placeholder {
  color: #94a3b8 !important;
  opacity: 1;
}

/* ไอคอนปฏิทิน */
.neo-input-modal .ant-picker-suffix {
  color: rgba(255,255,255,.8) !important;
}

/* ปุ่มล้าง */
.neo-input-modal .ant-picker-clear {
  background: transparent !important;
  color: rgba(255,255,255,.8) !important;
}

/* ===========================
   CHECKBOX
=========================== */

.neo-checkbox{
  color: rgba(var(--cinema-white-rgb), .88) !important;
  font-weight: 700;
}

.neo-checkbox .ant-checkbox{
  top: 0;
}

.neo-checkbox .ant-checkbox-inner{
  width: 20px;
  height: 20px;

  border-radius: 6px;

  background: rgba(var(--cinema-white-rgb), .06) !important;
  border: 1px solid rgba(var(--cinema-white-rgb), .15) !important;

  transition: .25s;
}

.neo-checkbox:hover .ant-checkbox-inner{
  border-color: var(--cinema-gold) !important;
  background: rgba(var(--cinema-gold-rgb), .08) !important;
}

.neo-checkbox .ant-checkbox-checked .ant-checkbox-inner{
  background: linear-gradient(
    135deg,
    var(--cinema-red),
    var(--cinema-gold)
  ) !important;

  border-color: transparent !important;
}

.neo-checkbox .ant-checkbox-checked::after{
  border-color: rgba(var(--cinema-gold-rgb), .35) !important;
}

.neo-checkbox .ant-checkbox-inner::after{
  border-color: #fff !important;
}

.neo-checkbox .ant-checkbox-disabled .ant-checkbox-inner{
  opacity: .45;
}

.neo-checkbox span:last-child{
  padding-inline-start: 10px;
}

/* ===========================
   PASSWORD
=========================== */

.neo-input-modal.ant-input-affix-wrapper{
  background:rgba(255,255,255,.06) !important;
  border:1px solid rgba(255,255,255,.08) !important;
  box-shadow:none !important;
}

/* input ข้างใน */
.neo-input-modal.ant-input-affix-wrapper > input.ant-input{
  background:transparent !important;
  color:#fff !important;
  -webkit-text-fill-color:#fff !important;

  border:none !important;
  box-shadow:none !important;

  font-weight:600;
}

/* placeholder */
.neo-input-modal.ant-input-affix-wrapper > input.ant-input::placeholder{
  color:#94a3b8 !important;
  -webkit-text-fill-color:#94a3b8 !important;
  opacity:1;
}

/* eye icon */
.neo-input-modal.ant-input-affix-wrapper .ant-input-password-icon{
  color:rgba(255,255,255,.55) !important;
}

/* hover */
.neo-input-modal.ant-input-affix-wrapper:hover{
  background:rgba(255,255,255,.08) !important;
  border-color:rgba(255,255,255,.12) !important;
}

/* focus */
.neo-input-modal.ant-input-affix-wrapper-focused,
.neo-input-modal.ant-input-affix-wrapper:focus-within{
  background:rgba(255,255,255,.08) !important;

  border-color:transparent !important;

  box-shadow:
    0 0 0 2px rgba(255,255,255,.08) !important;
}



/* ===========================
   NETFLIX SCREEN LIST
=========================== */

.neo-screen-list {
  display: flex;
  flex-direction: column;
  gap: 10px;

  margin-top: 4px;
}

/* แต่ละ row */
.neo-screen-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

/* input */
.neo-screen-input.ant-input-number {
  width: 100% !important;
  height: 44px !important;

  border-radius: 12px !important;

  background: rgba(255, 255, 255, 0.06) !important;

  border: 1px solid rgba(255, 255, 255, 0.08) !important;

  box-shadow: none !important;

  overflow: hidden;
}

.neo-screen-input.ant-input-number
  .ant-input-number-input {
  height: 42px !important;

  padding: 0 13px !important;

  color: #fff !important;
  -webkit-text-fill-color: #fff !important;

  font-weight: 600;
}

.neo-screen-input.ant-input-number
  .ant-input-number-input::placeholder {
  color: rgba(255, 255, 255, 0.35) !important;
  -webkit-text-fill-color: rgba(255, 255, 255, 0.35) !important;
}

/* hover / focus */
.neo-screen-input.ant-input-number:hover,
.neo-screen-input.ant-input-number-focused {
  border-color: rgba(252, 149, 15, 0.45) !important;

  box-shadow:
    0 0 0 2px rgba(252, 149, 15, 0.08) !important;
}

/* ซ่อนปุ่ม + - ของ InputNumber */
.neo-screen-input .ant-input-number-handler-wrap {
  display: none !important;
}

/* ปุ่มลบ */
.neo-screen-remove {
  height: 44px !important;

  padding: 0 14px !important;

  border-radius: 12px !important;

  background: rgba(248, 113, 113, 0.08) !important;

  border: 1px solid rgba(248, 113, 113, 0.18) !important;

  color: #f87171 !important;

  font-weight: 700 !important;
}

.neo-screen-remove:hover {
  background: rgba(248, 113, 113, 0.14) !important;

  border-color: rgba(248, 113, 113, 0.35) !important;

  color: #fca5a5 !important;
}

/* เพิ่ม Screen */
.neo-add-screen-btn {
  width: fit-content !important;
  height: 40px !important;

  padding: 0 14px !important;

  border-radius: 12px !important;

  background: rgba(252, 149, 15, 0.06) !important;

  border: 1px dashed rgba(252, 149, 15, 0.35) !important;

  color: #fc950f !important;

  font-size: 13px !important;
  font-weight: 700 !important;
}

.neo-add-screen-btn:hover {
  background: rgba(252, 149, 15, 0.1) !important;

  border-color: rgba(252, 149, 15, 0.6) !important;

  color: #ffb347 !important;
}
 `}</style>
    </>
  );
}
